import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { produce } from 'immer';
import { filter } from 'rxjs/operators';
import { NgxsStateFacade } from './ngxs-state-facade';

export interface LoadingStateModel {
  showLoadingBar: boolean;
  showSpinner: boolean;
}

export class ApiRequestStarted {
  static readonly type = '[Loading] Api Request Started';
}

export class ApiRequestComplete {
  static readonly type = '[Loading] Api Request Complete';
}

export class SetShowSpinner {
  static readonly type = '[Loading] Set Show Spinner';
  constructor(public show: boolean) {}
}

@State<LoadingStateModel>({
  name: 'loading',
  defaults: {
    showLoadingBar: false,
    showSpinner: true,
  },
})
@Injectable()
export class LoadingState extends NgxsStateFacade<LoadingStateModel> {
  @Selector()
  static showBar(state: LoadingStateModel): boolean {
    return state.showLoadingBar;
  }

  @Selector()
  static showSpinner(state: LoadingStateModel): boolean {
    return state.showSpinner;
  }

  @Selector()
  static isLoading(state: LoadingStateModel): boolean {
    return state.showLoadingBar || state.showSpinner;
  }

  get showBar(): boolean {
    return this.snapshot.showLoadingBar;
  }

  get showSpinner(): boolean {
    return this.snapshot.showSpinner;
  }

  get isLoading(): boolean {
    return this.showBar || this.showSpinner;
  }

  constructor(
    private readonly router: Router,
    store: Store,
  ) {
    super(store, 'loading');
    // Show spinner when navigation starts
    router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart)).subscribe(() => {
      this.setShowSpinner(true);
    });

    // Hide spinner when navigation is canceled (usually because of a guard)
    router.events.pipe(filter((e): e is NavigationCancel => e instanceof NavigationCancel)).subscribe(() => {
      this.setShowSpinner(false);
    });

    // Hide spinner when navigation ends
    router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
      this.setShowSpinner(false);
    });
  }

  apiRequestStarted(): void {
    this.store.dispatch(new ApiRequestStarted());
  }

  apiRequestComplete(): void {
    this.store.dispatch(new ApiRequestComplete());
  }

  setShowSpinner(show: boolean): void {
    this.store.dispatch(new SetShowSpinner(show));
  }

  @Action(ApiRequestStarted)
  apiRequestStartedHandler(ctx: StateContext<LoadingStateModel>): void {
    setTimeout(() => {
      ctx.setState(
        produce(ctx.getState(), (draft) => {
          draft.showLoadingBar = true;
        }),
      );
    });
  }

  @Action(ApiRequestComplete)
  apiRequestCompleteHandler(ctx: StateContext<LoadingStateModel>): void {
    setTimeout(() => {
      ctx.setState(
        produce(ctx.getState(), (draft) => {
          draft.showLoadingBar = false;
        }),
      );
    });
  }

  @Action(SetShowSpinner)
  setShowSpinnerHandler(ctx: StateContext<LoadingStateModel>, action: SetShowSpinner): void {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.showSpinner = action.show;
      }),
    );
  }
}
